import React, { useEffect, useState } from 'react';
import { getAllLicenses, getAllUsers, resetHWID, revokeLicense, createLicense, deleteUser, updateUser, suspendUser } from '../services/adminService';
import { License } from '../types/License';
import { User } from '../types/User';
import { Users, Key, RefreshCw, XCircle, AlertTriangle, Plus, Trash2, Edit2, Ban } from 'lucide-react';
import { toast } from '../components/ui/Toast';
import { useDialog } from '../components/ui/Dialog';
import Button from '../components/ui/Button';

const AdminDashboard: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'licenses' | 'users'>('licenses');
  const [loading, setLoading] = useState(true);
  const { showDialog } = useDialog();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [licensesData, usersData] = await Promise.all([
        getAllLicenses(),
        getAllUsers()
      ]);
      setLicenses(licensesData);
      setUsers(usersData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch data. Please try again.",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLicense = () => {
    let formData = {
      email: '',
      cost: '',
      orderId: ''
    };

    const content = (
      <div className="space-y-4">
        <div>
          <label className="block mb-1 text-sm font-medium text-dark-200">Email</label>
          <input
            type="email"
            onChange={(e) => formData.email = e.target.value}
            className="px-3 py-2 w-full rounded-md border bg-dark-900 border-dark-600 text-dark-100 focus:outline-none focus:ring-0 focus:border-primary-500"
            placeholder="user@example.com"
            required
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-dark-200">Cost (USD)</label>
          <input
            type="number"
            step="0.01"
            onChange={(e) => formData.cost = e.target.value}
            className="px-3 py-2 w-full rounded-md border bg-dark-900 border-dark-600 text-dark-100 focus:outline-none focus:ring-0 focus:border-primary-500"
            placeholder="0.00"
            required
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-dark-200">Order ID</label>
          <input
            type="text"
            onChange={(e) => formData.orderId = e.target.value}
            className="px-3 py-2 w-full rounded-md border bg-dark-900 border-dark-600 text-dark-100 focus:outline-none focus:ring-0 focus:border-primary-500"
            placeholder="ORD-XXXXX"
            required
          />
        </div>
      </div>
    );

    showDialog({
      title: "Create New License",
      content,
      confirmLabel: "Create",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        if (!formData.email || !formData.cost || !formData.orderId) {
          toast({
            title: "Error",
            description: "All fields are required",
            type: "error"
          });
          return;
        }

        try {
          await createLicense(formData.email, Number(formData.cost), formData.orderId);
          toast({
            title: "Success",
            description: "License created successfully",
            type: "success"
          });
          fetchData();
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to create license",
            type: "error"
          });
          throw error;
        }
      }
    });
  };

  const handleEditUser = (user: User) => {
    let formData = {
      email: user.email,
      isAdmin: user.isAdmin
    };

    const content = (
      <div className="space-y-4">
        <div>
          <label className="block mb-1 text-sm font-medium text-dark-200">Email</label>
          <input
            type="email"
            defaultValue={user.email}
            onChange={(e) => formData.email = e.target.value}
            className="px-3 py-2 w-full rounded-md border bg-dark-900 border-dark-600 text-dark-100 focus:outline-none focus:ring-0 focus:border-primary-500"
            placeholder="user@example.com"
            required
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isAdmin"
            defaultChecked={user.isAdmin}
            onChange={(e) => formData.isAdmin = e.target.checked}
            className="mr-2"
          />
          <label htmlFor="isAdmin" className="text-sm font-medium text-dark-200">Admin Access</label>
        </div>
      </div>
    );

    showDialog({
      title: "Edit User",
      content,
      confirmLabel: "Update",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        await updateUser(user.id, formData);
        fetchData();
      }
    });
  };

  const handleDeleteUser = async (userId: string) => {
    showDialog({
      title: "Delete User",
      description: "Are you sure you want to delete this user? This action cannot be undone and will delete all associated licenses.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
      onConfirm: async () => {
        await deleteUser(userId);
        fetchData();
      }
    });
  };

  const handleSuspendUser = async (userId: string, isSuspended: boolean) => {
    showDialog({
      title: isSuspended ? "Unsuspend User" : "Suspend User",
      description: isSuspended
        ? "Are you sure you want to unsuspend this user? This will allow them to use their licenses again."
        : "Are you sure you want to suspend this user? This will revoke all their active licenses.",
      confirmLabel: isSuspended ? "Unsuspend" : "Suspend",
      cancelLabel: "Cancel",
      variant: "warning",
      onConfirm: async () => {
        await suspendUser(userId);
        fetchData();
      }
    });
  };

  const handleResetHWID = async (licenseKey: string) => {
    showDialog({
      title: "Reset Hardware ID",
      description: "Are you sure you want to reset the hardware ID for this license? The user will need to reactivate their software.",
      confirmLabel: "Reset",
      cancelLabel: "Cancel",
      variant: "warning",
      onConfirm: async () => {
        await resetHWID(licenseKey);
        fetchData();
      }
    });
  };

  const handleRevokeLicense = async (licenseKey: string) => {
    showDialog({
      title: "Revoke License",
      description: "Are you sure you want to revoke this license? The user will no longer be able to use the software with this license key.",
      confirmLabel: "Revoke",
      cancelLabel: "Cancel",
      variant: "danger",
      onConfirm: async () => {
        await revokeLicense(licenseKey);
        fetchData();
      }
    });
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 rounded-full border-b-2 animate-spin border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="overflow-hidden rounded-lg border shadow-lg bg-dark-800 border-dark-700">
        <div className="px-6 py-5 border-b border-dark-700">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium leading-6 text-dark-100">Admin Dashboard</h3>
              <p className="mt-1 text-sm text-dark-400">
                Manage licenses and users
              </p>
            </div>
            {activeTab === 'licenses' && (
              <Button
                variant="primary"
                icon={Plus} onClick={handleCreateLicense}
              >
                Create License
              </Button>
            )}
          </div>
        </div>

        <div className="border-b border-dark-700">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('licenses')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${activeTab === 'licenses'
                ? 'border-b-2 border-primary-500 text-primary-500'
                : 'text-dark-400 hover:text-dark-200'
                }`}
            >
              <Key className="mr-2 w-4 h-4" />
              Licenses
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 text-sm font-medium flex items-center ${activeTab === 'users'
                ? 'border-b-2 border-primary-500 text-primary-500'
                : 'text-dark-400 hover:text-dark-200'
                }`}
            >
              <Users className="mr-2 w-4 h-4" />
              Users
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'licenses' ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-dark-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">License Key</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Email</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Status</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Cost</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Expires</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">HWID</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-700">
                  {licenses.map((license) => (
                    <tr key={license.id} className="hover:bg-dark-700/50">
                      <td className="px-6 py-4 font-mono text-sm whitespace-nowrap text-dark-300">{license.key}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">{license.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${license.status === 'active' ? 'bg-green-900 text-green-300' :
                          license.status === 'expired' ? 'bg-yellow-900 text-yellow-300' :
                            'bg-red-900 text-red-300'
                          }`}>
                          {license.status.charAt(0).toUpperCase() + license.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">{formatCurrency(Number(license.cost))}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">{formatDate(license.expiresAt)}</td>
                      <td className="px-6 py-4 font-mono text-sm whitespace-nowrap text-dark-300">
                        {license.hwid || 'Not bound'}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">
                        <div className="flex">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetHWID(license.key)}
                          >
                            <RefreshCw className="w-4 h-4 text-yellow-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRevokeLicense(license.key)}
                          >
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-dark-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Email</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Role</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Status</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Created At</th>
                    <th className="px-6 py-3 text-xs font-medium tracking-wider text-left uppercase text-dark-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-700">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-dark-700/50">
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isAdmin ? 'bg-primary-900 text-primary-300' : 'bg-dark-700 text-dark-300'
                          }`}>
                          {user.isAdmin ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isSuspended ? 'bg-red-900 text-red-300' : 'bg-green-900 text-green-300'
                          }`}>
                          {user.isSuspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap text-dark-300">
                        <div className="flex">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit2 className="w-4 h-4 text-primary-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSuspendUser(user.id, user.isSuspended)}
                          >
                            <Ban className="w-4 h-4 text-yellow-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;